/**
 * [AB#1173] O ponteiro do job vive no localStorage, mas o ARQUIVO enviado não cabe lá (binário, MBs). Sem ele, a prévia
 * restaurada ao reabrir o modal de carga em massa não consegue confirmar (a confirmação reenvia o mesmo arquivo). O
 * arquivo fica no IndexedDB, com estas regras (dado de cliente em disco do navegador — vida curta e isolada):
 *  - chave = tenant + usuário + carregador (`chaveArquivo`): nunca se lê arquivo de outro usuário/tenant;
 *  - apagado ao confirmar, descartar, concluir/falhar o job, logout, troca de usuário/tenant e após 24 h (`TTL_ARQUIVO_MS`);
 *  - `limparExpirados()` roda na abertura do app; `limparTodosArquivos()` roda em `useAuthStore.clear()` (logout, sessão
 *    inválida, fim de impersonate) e quando entra outra pessoa (`setSession`).
 * Tudo é "melhor esforço": sem IndexedDB (modo privado, quota, testes) resolve sem erro e o modal pede o reenvio.
 */
export const TTL_ARQUIVO_MS = 24 * 3_600_000;
const BANCO = 'vinya-carga-em-massa';
const LOJA = 'arquivos';

interface Registro {
  nome: string;
  tipo: string;
  blob: Blob;
  em: number;
}

export function chaveArquivo(tenantId: number, usuarioId: number, carregador: string): string {
  return `${tenantId}:${usuarioId}:${carregador}`;
}

/** `tenant_id` do access token atual (0 quando não há token/legível) — o `Usuario` do store não carrega o tenant. */
export function tenantDoToken(accessToken: string | null | undefined): number {
  try {
    const payload = accessToken?.split('.')[1];
    if (!payload) return 0;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { tenant_id?: number };
    return typeof json.tenant_id === 'number' ? json.tenant_id : 0;
  } catch {
    return 0;
  }
}

function abrir(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(BANCO, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(LOJA);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function transacao<T>(modo: IDBTransactionMode, fn: (loja: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  const db = await abrir();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const req = fn(db.transaction(LOJA, modo).objectStore(LOJA));
      req.onsuccess = () => {
        db.close();
        resolve(req.result);
      };
      req.onerror = () => {
        db.close();
        resolve(null);
      };
    } catch {
      db.close();
      resolve(null);
    }
  });
}

export async function salvarArquivoPersistido(chave: string, arquivo: File): Promise<void> {
  const r: Registro = { nome: arquivo.name, tipo: arquivo.type, blob: arquivo, em: Date.now() };
  await transacao('readwrite', (l) => l.put(r, chave));
}

export async function apagarArquivoPersistido(chave: string): Promise<void> {
  await transacao('readwrite', (l) => l.delete(chave));
}

/** Devolve o arquivo da chave — expirado (> 24 h) é apagado e some. */
export async function lerArquivoPersistido(chave: string): Promise<File | null> {
  const r = (await transacao('readonly', (l) => l.get(chave))) as Registro | null | undefined;
  if (!r?.blob) return null;
  if (Date.now() - r.em > TTL_ARQUIVO_MS) {
    await apagarArquivoPersistido(chave);
    return null;
  }
  return new File([r.blob], r.nome, { type: r.tipo });
}

/** Remove todo registro com mais de 24 h (chamado na abertura do app). Devolve quantos removeu. */
export async function limparExpirados(): Promise<number> {
  const db = await abrir();
  if (!db) return 0;
  return new Promise((resolve) => {
    let removidos = 0;
    try {
      const tx = db.transaction(LOJA, 'readwrite');
      const req = tx.objectStore(LOJA).openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return;
        if (Date.now() - (cur.value as Registro).em > TTL_ARQUIVO_MS) {
          cur.delete();
          removidos++;
        }
        cur.continue();
      };
      tx.oncomplete = () => {
        db.close();
        resolve(removidos);
      };
      tx.onerror = tx.onabort = () => {
        db.close();
        resolve(removidos);
      };
    } catch {
      db.close();
      resolve(removidos);
    }
  });
}

/** Esvazia tudo (logout, sessão inválida, troca de usuário/tenant). */
export async function limparTodosArquivos(): Promise<void> {
  await transacao('readwrite', (l) => l.clear());
}
